import {
  type AfterViewInit,
  computed,
  DestroyRef,
  Directive,
  effect,
  inject,
  input,
  NgZone,
  numberAttribute,
  output,
} from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { debounceTime, filter, switchMap, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { Subscription } from 'rxjs';
import { fromEvent, of } from 'rxjs';

/** The height of the select items in `em` units. */
const SELECT_ITEM_HEIGHT_EM = 3;

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'mat-select[msInfiniteScroll]',
})
export class MatSelectInfiniteScrollDirective implements AfterViewInit {
  readonly #destroyRef = inject(DestroyRef);
  readonly #matSelect = inject(MatSelect);
  readonly #ngZone = inject(NgZone);
  readonly #unit = computed(() => (this.threshold().lastIndexOf('%') > -1 ? '%' : 'px'));
  readonly #thrPx = computed(() => (this.#unit() === '%' ? 0 : parseFloat(this.threshold()) / 100));
  readonly #thrPercent = computed(() =>
    this.#unit() === 'px' ? 0 : parseFloat(this.threshold()) / 100,
  );
  #selectItemHeightPx!: number;
  #panel!: Element;
  #registerScrollListenerSubscription: Subscription | undefined;

  readonly threshold = input('15%');
  readonly debounceTime = input(100, { transform: numberAttribute });
  readonly complete = input(false);
  readonly infiniteScroll = output<void>();

  constructor() {
    effect(() => {
      if (this.complete()) {
        this.#registerScrollListenerSubscription?.unsubscribe();
      }
    });
  }

  ngAfterViewInit(): void {
    this.#matSelect.openedChange
      .pipe(
        filter(opened => opened),
        switchMap(() => (this.#ngZone.isStable ? of('') : this.#ngZone.onStable.pipe(take(1)))),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        const panel = this.#matSelect.panel.nativeElement;
        this.#initialize(panel);

        this.#registerScrollListener(() => this.infiniteScroll.emit());
      });
  }

  #getSelectItemHeightPx(panel: Element): number {
    return parseFloat(getComputedStyle(panel).fontSize) * SELECT_ITEM_HEIGHT_EM;
  }

  #initialize(panel: Element): void {
    this.#panel = panel;
    this.#selectItemHeightPx = this.#getSelectItemHeightPx(panel);
  }

  #registerScrollListener(infiniteScrollCallback: () => void): void {
    this.#ngZone.runOutsideAngular(() => {
      this.#registerScrollListenerSubscription = fromEvent(this.#panel, 'scroll')
        .pipe(takeUntilDestroyed(this.#destroyRef), debounceTime(this.debounceTime()))
        .subscribe(event => this.#handleScrollEvent(event, infiniteScrollCallback));
    });
  }

  #handleScrollEvent(event: any, infiniteScrollCallback: () => void): void {
    if (this.complete()) {
      return;
    }
    const countOfRenderedOptions = Math.round(this.#panel.scrollHeight / this.#selectItemHeightPx);
    const infiniteScrollDistance = this.#selectItemHeightPx * countOfRenderedOptions;
    const threshold =
      this.#unit() === '%' ? infiniteScrollDistance * this.#thrPercent() : this.#thrPx();

    const scrolledDistance = this.#panel.clientHeight + event.target.scrollTop;

    if (scrolledDistance + threshold >= infiniteScrollDistance) {
      this.#ngZone.run(infiniteScrollCallback);
    }
  }
}
